import { signInPathFor } from '@/utils/authSession';

/** Route fields the access rules read (a vue-router `RouteLocation` fits). */
export interface RouteTarget {
  name?: unknown;
  path: string;
  fullPath?: string;
  matched?: Array<{ name?: unknown; meta?: Record<string, unknown> }>;
}

// Pages anyone can open signed out.
const PUBLIC_ROUTE_NAMES = new Set([
  'WebAppStickerPublic',
  'StickerCheckout',
  'WebAppProfileBySlug',
  'WebAppProfile',
  'WebAppOrgProfile',
  'WebAppOrgProfileBySlug',
  'WebAppOrgProfileEmbed',
  'WebAppOrgProfileEmbedBySlug',
  'WebAppStickerEmbed',
  'WebAppStickerCheckoutEmbed',
  'WebAppStickerSuccessEmbed',
  'WebAppUserProfileBySlug',
  'WebAppStickerBySlugShort',
  'WebAppStickerCollection',
  'WebAppDashboard',
  'WebAppHome',
  'WebAppStaticPage',
  'CollectionDetail',
  'WebAppSearch',
  'SupportedCauses',
  'WebAppGlowDashboard',
  'WebAppGlowsPast',
  'WebAppGlowsUpcoming',
  'WebAppGlowPage',
  'WebAppGlowAnalytics',
  'ShareGlowPage',
  'SandiegoGives',
  'TicketCheckout',
  'TicketSuccess',
]);

const STICKER_FLOW_ROUTE_NAMES = new Set(['WebAppSticker', 'StickerPurchase']);

const AUTH_ROUTE_NAMES = new Set(['WebAppSignIn', 'WebAppInvite']);

// Invitation wizards (links in invite emails): the invitee has no account yet.
const ONBOARDING_PARENT_ROUTE_NAMES = new Set(['OnboardingView', 'UserOnboardingView']);

export function isPublicRoute(route: { name?: unknown }): boolean {
  return PUBLIC_ROUTE_NAMES.has(route.name as string);
}

function isAuthFlow(route: RouteTarget): boolean {
  return AUTH_ROUTE_NAMES.has(route.name as string)
    || route.path === '/auth'
    || route.path.startsWith('/auth/');
}

/** Whether `route` is a step of the org or user invitation wizard. */
export function isOnboardingFlow(route: RouteTarget): boolean {
  return !!route.matched?.some(
    (record) => ONBOARDING_PARENT_ROUTE_NAMES.has(record.name as string),
  );
}

function isPaymentFlow(route: RouteTarget): boolean {
  return route.name === 'InitiateWalletPaymentIntent' || route.path.includes('/payment/');
}

function isStickerFlow(route: RouteTarget): boolean {
  return STICKER_FLOW_ROUTE_NAMES.has(route.name as string);
}

/** Sign-in, payment and sticker pages handle their own session; the guard leaves the token alone. */
export function skipsTokenCheck(route: RouteTarget): boolean {
  return isAuthFlow(route) || isPaymentFlow(route) || isStickerFlow(route);
}

/**
 * Whether `route` stays open after the session ends (public, sign-in, payment, sticker and
 * invitation pages).
 */
export function isOpenWhenSignedOut(route: RouteTarget): boolean {
  return skipsTokenCheck(route)
    || isPublicRoute(route)
    || isOnboardingFlow(route)
    || route.path.startsWith('/.well-known/');
}

/** Whether `to` (or one of its parent routes) is a super-admin CMS page. */
export function isAdminOnlyRoute(to: RouteTarget): boolean {
  return !!to.matched?.some((record) => record.meta?.adminOnly);
}

/** Where the guard sends a navigation to `to`, or null to let it through. */
export function guardRedirect(
  to: RouteTarget,
  isSignedIn: boolean,
  isAdmin = false,
): string | null {
  if (!isSignedIn) {
    return isOpenWhenSignedOut(to) ? null : signInPathFor(to.fullPath ?? to.path);
  }
  if (isAdminOnlyRoute(to) && !isAdmin) return '/dashboard';
  return null;
}

function isUnder(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

/** Where a URL that matches no route goes: web app home for web paths, the CMS otherwise. */
export function unknownPathRedirect(path: string): string {
  return isUnder(path, '/web') || isUnder(path, '/auth/web') ? '/web/' : '/dashboard';
}
